import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AsOfDateService } from '../services/as-of-date.service';

/**
 * Time-Travel toolbar button.
 *
 * Lives next to <notifications> in every layout. One small clock icon —
 * neutral when in real-time, amber + small dot when an as-of-date is set.
 * Click opens a popover with the date picker + reset.
 *
 * Replaces the earlier full-width banner, which pushed the Fuse layout
 * hierarchy and blocked toolbar buttons. An icon in the toolbar is
 * always-visible without taking layout space.
 */
@Component({
    selector: 'tma-time-travel-banner',
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <button mat-icon-button
                [matTooltip]="tooltip()"
                [matMenuTriggerFor]="menu"
                class="relative">
            <mat-icon
                class="icon-size-5"
                [class.text-amber-600]="asOf.isTimeTravel()"
                [class.dark:text-amber-400]="asOf.isTimeTravel()">
                schedule
            </mat-icon>
            @if (asOf.isTimeTravel()) {
                <!-- Status dot — amber when active so the user can tell at a glance -->
                <span class="absolute top-1.5 right-1.5 h-2 w-2 rounded-full
                             bg-amber-500 ring-2 ring-white dark:ring-gray-900"></span>
            }
        </button>

        <mat-menu #menu="matMenu" class="!mt-1" xPosition="before">
            <div class="w-72 p-3" (click)="$event.stopPropagation()">
                <div class="flex items-center gap-2 mb-2">
                    <mat-icon class="text-amber-600 icon-size-4">schedule</mat-icon>
                    <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Time-travel
                    </span>
                </div>

                @if (asOf.isTimeTravel()) {
                    <p class="text-xs text-amber-700 dark:text-amber-400 m-0 mb-2">
                        Viewing as of <strong>{{ asOf.asOfDate() }}</strong> ({{ daysFromToday() }}).
                        Every dashboard, KPI, and aging calculation reflects that date.
                    </p>
                } @else {
                    <p class="text-xs text-gray-500 dark:text-gray-400 m-0 mb-2">
                        Set "today" to a different date to fool every dashboard, KPI,
                        and aging calculation into computing as if that were today.
                    </p>
                }

                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    As of date
                </label>
                <input type="date"
                       [value]="dateInput()"
                       (change)="onDateChange($any($event.target).value)"
                       class="w-full px-2 py-1.5 rounded border bg-white dark:bg-gray-800
                              border-gray-300 dark:border-gray-600
                              text-sm text-gray-900 dark:text-gray-100
                              focus:outline-none focus:ring-2 focus:ring-amber-500" />

                @if (asOf.isTimeTravel()) {
                    <button mat-stroked-button
                            (click)="asOf.clear()"
                            class="!w-full !mt-2 !text-amber-700 !border-amber-700">
                        <mat-icon class="!mr-1 icon-size-4">close</mat-icon>
                        Reset to today
                    </button>
                }
            </div>
        </mat-menu>
    `,
})
export class TimeTravelBannerComponent {
    readonly asOf = inject(AsOfDateService);

    readonly dateInput = computed(() => this.asOf.asOfDate() ?? '');

    readonly tooltip = computed(() => {
        const o = this.asOf.asOfDate();
        return o ? `Time-travel: ${o} (${this.daysFromToday()})` : 'Time-travel (real-time)';
    });

    readonly daysFromToday = computed(() => {
        const o = this.asOf.asOfDate();
        if (!o) return 'today';
        const [y, m, d] = o.split('-').map(Number);
        const target = new Date(y, m - 1, d).getTime();
        const today  = new Date(); today.setHours(0, 0, 0, 0);
        const days   = Math.round((today.getTime() - target) / 86_400_000);
        if (days === 0) return 'today';
        if (days > 0)   return `${days} day${days === 1 ? '' : 's'} ago`;
        return `${-days} day${-days === 1 ? '' : 's'} from now`;
    });

    onDateChange(value: string): void {
        if (!value) { this.asOf.clear(); return; }
        this.asOf.set(value);
    }
}
